import { EventRoleDTO } from "@/types/EventRoleDTO";
import {
  acceptEventRole,
  declineEventRole,
  getPaginatedEventRoles,
  createCollaboratorEventRoles as createCollaboratorEventRolesApi,
  deleteEventRole as deleteEventRoleApi,
  updateEventRole as updateEventRoleApi,
  getEventRoleByUserId as getEventRoleByUserIdApi,
  initiateEventOwnerChange as initiateEventOwnerChangeApi,
  handoffEventOwner as handoffEventOwnerApi,
  getTransferInvitation as getTransferInvitationApi,
  cancelTransferInvitation as cancelTransferInvitationApi,
  type InviteOutcome,
} from "@/lib/api/clients/EventRoleClient";
import { handleErrorServer, handleErrorServerWithStatus, ServerError } from "@/lib/api/errors/ErrorHandler";
import { EventRoleCreationDTO } from "@/types/EventRoleCreationDTO";
import { EventRole } from "@/types/EventRole";
import type { TransferInvitation, TransferStatus } from "@/types/TransferInvitation";
import { isValidObjectId } from "@/lib/utils/input-validator";

export async function getEventRoleByUserId(
  eventId: string,
  fields: string = "role",
): Promise<EventRoleDTO | ServerError> {
  try {
    return await getEventRoleByUserIdApi(eventId, fields);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function acceptEventInvitation(
  eventId: string,
): Promise<void | ServerError> {
  try {
    return await acceptEventRole(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteEventRole(
  eventId: string,
  email: string,
): Promise<void | ServerError> {
  try {
    return await deleteEventRoleApi(eventId, email);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEventRole(eventRole: EventRole) {
  try {

    if (!eventRole.eventId) {
      throw new Error("Event ID is required for updating the role.");
    }

    if (!eventRole.eventRoleId) {
      throw new Error("Event Role ID is required for updating the role.");
    }

    if (!eventRole.role) {
      throw new Error("Role is required for updating the role.");
    }

    return await updateEventRoleApi(eventRole);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function declineEventInvitation(
  eventId: string,
  type: string,
): Promise<void | ServerError> {
  try {

    if (type !== "EVENT_INVITATION" && type !== "EVENT_TRANSFER_INVITATION") {
      throw new Error("validationError");
    }

    return await declineEventRole(eventId, type);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getEventUsers(
  eventId: string,
  page: number,
  pageSize: number,
): Promise<EventRoleDTO[] | ServerError> {
  try {

    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }

    return await getPaginatedEventRoles(eventId, page, pageSize);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Same-company instant owner hand-off. Caller (event owner) becomes ADMIN,
 * the target becomes OWNER; event stays in its company.
 */
export async function handoffEventOwner(
  eventId: string,
  newOwnerId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(newOwnerId)) {
      throw new Error("validationError");
    }

    return await handoffEventOwnerApi(eventId, newOwnerId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Cross-company transfer invitation. Target must be an active collaborator
 * AND the owner of their company; accepting moves the event to their company.
 */
export async function initiateEventOwnerChange(
  eventId: string,
  newOwnerId: string,
  /** Offboarding: only with the strip flag on. Omitted = today's body. */
  keepInitiatorAsAdmin?: boolean,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(newOwnerId)) {
      throw new Error("validationError");
    }

    return typeof keepInitiatorAsAdmin === "boolean"
      ? await initiateEventOwnerChangeApi(eventId, newOwnerId, keepInitiatorAsAdmin)
      : await initiateEventOwnerChangeApi(eventId, newOwnerId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Pending-transfer status for the collaboration settings banner and the
 * transfer dialog. BE always answers 200 with {pending, ...}; this unwraps to
 * the invitation (or null when nothing is pending) plus the offboarding strip
 * flag, which the backend sends in both branches — the dialog needs it before
 * anything is pending. Only a literal `true` turns it on.
 */
export async function getEventTransferInvitation(
  eventId: string,
): Promise<TransferStatus | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    const status = await getTransferInvitationApi(eventId);
    const transferStripEnabled = status?.transferStripEnabled === true;
    if (!status?.pending) return { invitation: null, transferStripEnabled };
    const invitation: TransferInvitation = {
      targetUserId: status.targetUserId ?? "",
      targetEmail: status.targetEmail ?? "",
      targetName: status.targetName ?? undefined,
      createdAt: status.createdAt ?? "",
      expiresAt: status.expiresAt ?? "",
    };
    if (typeof status.initiatedByUserId === "string" && status.initiatedByUserId.length > 0) {
      invitation.initiatedByUserId = status.initiatedByUserId;
    }
    if (typeof status.keepInitiator === "boolean") {
      invitation.keepInitiator = status.keepInitiator;
    }
    return { invitation, transferStripEnabled };
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function cancelEventTransferInvitation(
  eventId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await cancelTransferInvitationApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function createCollaboratorEventRoles(
  eventId: string,
  eventRoles: EventRoleCreationDTO[],
): Promise<InviteOutcome | ServerError> {
  try {

    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }

    return await createCollaboratorEventRolesApi(
      eventId,
      eventRoles,
    );
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}
